/**
 * Platform admin.
 *
 * Note what this screen cannot do: there is no way to list, open or download a
 * group's photos, because the API offers no procedure that would let it. An
 * administrator who needs to see content has to be invited into the group like
 * anyone else, which leaves an audit trail. The panel says so out loud — an
 * invisible guarantee reassures nobody.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { formatBytes } from "@fc/core";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { Badge } from "../../components/ui/Badge.js";
import { Button } from "../../components/ui/Button.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { ProgressBar } from "../../components/ui/ProgressBar.js";
import { SegmentedControl } from "../../components/ui/SegmentedControl.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { StorageMeter } from "../../components/ui/StorageMeter.js";
import { useToast } from "../../components/ui/Toast.js";
import { AccessCodesPanel } from "./AccessCodesPanel.js";
import { AdminTabs, AdminTabPanel, type AdminTab } from "./AdminTabs.js";
import { DeletedGroups } from "./DeletedGroups.js";
import { WaitlistPanel } from "./WaitlistPanel.js";
import { SecurityPanel } from "./SecurityPanel.js";
import "./AdminPage.css";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";
import { FindByEmail } from "./FindByEmail.js";
import { GroupRecovery } from "./GroupRecovery.js";

type TabId =
  | "overview"
  | "groups"
  | "people"
  | "waitlist"
  | "security"
  | "storage"
  | "jobs";

/*
 * Grouped by the question being asked, not by which router the data comes from.
 * "Am I admitting somebody" and "is a key about to expire" are different jobs
 * even though both are, underneath, administration.
 */
const TABS: AdminTab<TabId>[] = [
  { id: "overview", label: "Overview" },
  { id: "groups", label: "Groups" },
  { id: "people", label: "People" },
  { id: "waitlist", label: "Waitlist" },
  { id: "security", label: "Security" },
  { id: "storage", label: "Storage" },
  { id: "jobs", label: "Processing" },
];

export function AdminPage() {
  const stats = useQuery(trpc.admin.stats.queryOptions());

  const [query, setQuery] = useState("");
  const [lookupId, setLookupId] = useState<string | null>(null);
  // Drives the header's hairline: it only appears once there is content above
  // the fold for it to separate.
  const [scrolled, setScrolled] = useState(false);
  const [tab, setTab] = useState<TabId>("overview");

  const found = useQuery({
    ...trpc.admin.findGroup.queryOptions({ groupId: lookupId ?? "" }),
    enabled: lookupId !== null && lookupId.length >= 3,
    // A typo'd id is a NOT_FOUND, not a blip — retrying it just delays the
    // answer.
    retry: false,
  });

  return (
    /*
     * The page is its own scroll container. `.fc-main` is a bounded grid row,
     * so scrolling here rather than on `body` is what keeps the header pinned
     * and the mobile bottom navigation on screen at the same time.
     */
    <div
      className="fc-admin"
      onScroll={(event) => setScrolled(event.currentTarget.scrollTop > 0)}
    >
      <header
        className="fc-admin__bar"
        data-scrolled={scrolled ? "" : undefined}
      >
        <div className="fc-admin__bar-inner">
          <Link className="fc-admin__back" to="/" aria-label="Back to home">
            <BackIcon />
          </Link>
          <h1 className="fc-admin__title">Platform admin</h1>
        </div>
      </header>

      <div className="fc-admin__content">
        <p className="fc-admin__privacy" role="note">
          This panel administers groups — storage, locks and processing. It
          cannot show you anyone&rsquo;s photos, by design: there is no
          procedure behind it that returns group content.
        </p>

        <AdminTabs tabs={TABS} active={tab} onChange={setTab} />

        <AdminTabPanel id="overview" active={tab === "overview"}>
          <section
            className="fc-admin__section"
            aria-labelledby="fc-admin-stats"
          >
            <h2 className="fc-admin__section-title" id="fc-admin-stats">
              Across the platform
            </h2>

            {stats.isLoading ? (
              <div className="fc-admin__stats" aria-hidden="true">
                {Array.from({ length: 4 }, (_, index) => (
                  <Skeleton key={index} height="88px" radius="md" />
                ))}
              </div>
            ) : stats.isError || !stats.data ? (
              <p className="fc-admin__error" role="alert">
                {errorMessage(stats.error)}
              </p>
            ) : (
              <>
                <dl className="fc-admin__stats">
                  <Counter
                    label="Groups"
                    value={stats.data.groupCount.toLocaleString()}
                  />
                  <Counter
                    label="People"
                    value={stats.data.userCount.toLocaleString()}
                  />
                  <Counter
                    label="Files"
                    value={stats.data.fileCount.toLocaleString()}
                  />
                  <Counter
                    label="Storage used"
                    value={formatBytes(stats.data.usedBytes)}
                    hint={`of ${formatBytes(stats.data.allocatedBytes)} allocated`}
                  />
                </dl>

                <ProgressBar
                  label="Allocated storage in use"
                  value={
                    stats.data.allocatedBytes > 0
                      ? stats.data.usedBytes / stats.data.allocatedBytes
                      : 0
                  }
                />
              </>
            )}
          </section>
        </AdminTabPanel>

        <AdminTabPanel id="groups" active={tab === "groups"}>
          <section
            className="fc-admin__section"
            aria-labelledby="fc-admin-find"
          >
            <h2 className="fc-admin__section-title" id="fc-admin-find">
              Find a group
            </h2>

            <form
              className="fc-admin__search"
              onSubmit={(event) => {
                event.preventDefault();
                setLookupId(query.trim());
              }}
            >
              <Field
                label="Group ID"
                hint="The identifier from a support request, e.g. grp_01j… Names aren't searchable here on purpose."
                className="fc-admin__search-field"
              >
                <Input
                  value={query}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="grp_…"
                />
              </Field>
              <Button
                type="submit"
                variant="primary"
                disabled={query.trim().length < 3}
              >
                Look up
              </Button>
            </form>

            {/*
              The other way in. Every recovery procedure starts with a group
              ID, and until now the only way to get one was to already have it.
            */}
            <FindByEmail
              onPick={(groupId) => {
                setQuery(groupId);
                setLookupId(groupId);
              }}
            />

            {found.isFetching ? (
              <Skeleton height="200px" radius="md" />
            ) : found.isError ? (
              <p className="fc-admin__error" role="alert">
                {errorMessage(found.error)}
              </p>
            ) : found.data ? (
              <GroupPanel key={found.data.id} group={found.data} />
            ) : (
              <EmptyState
                icon={<SearchIcon />}
                title="No group loaded"
                description="Paste a group ID above to see its storage, lock state and processing health."
              />
            )}
          </section>
        </AdminTabPanel>

        <AdminTabPanel id="people" active={tab === "people"}>
          <AccessCodesPanel />
        </AdminTabPanel>

        <AdminTabPanel id="waitlist" active={tab === "waitlist"}>
          <WaitlistPanel />
        </AdminTabPanel>

        <AdminTabPanel id="security" active={tab === "security"}>
          <SecurityPanel />
        </AdminTabPanel>

        <AdminTabPanel id="storage" active={tab === "storage"}>
          <DeletedGroups />
        </AdminTabPanel>

        <AdminTabPanel id="jobs" active={tab === "jobs"}>
          <JobsSection />
        </AdminTabPanel>
      </div>
    </div>
  );
}

/* ─────────────────────────────── Group panel ──────────────────────────────── */

interface FoundGroup {
  id: string;
  name: string;
  emoji: string | null;
  status: "active" | "locked" | "deleting" | "deleted";
  lockedReason: string | null;
  limitBytes: number | null;
  usedBytes: number | null;
  reservedBytes: number | null;
  usedOriginalsBytes: number | null;
  usedDerivativesBytes: number | null;
  usedVideoBytes: number | null;
  usedExportBytes: number | null;
  fileCount: number | null;
  memberCount: number;
  failedJobCount: number;
}

function GroupPanel({ group }: { group: FoundGroup }) {
  const usedBytes = group.usedBytes ?? 0;
  const limitBytes = group.limitBytes ?? 0;

  return (
    <div className="fc-admin__group">
      <div className="fc-admin__group-head">
        <h3 className="fc-admin__group-name">
          {group.emoji && (
            <span aria-hidden="true">
              <EmojiIcon emoji={group.emoji} />{" "}
            </span>
          )}
          {group.name}
        </h3>
        <Badge variant={group.status === "active" ? undefined : "warning"}>
          {group.status}
        </Badge>
      </div>

      <p className="fc-admin__group-id fc-tabular">{group.id}</p>

      <dl className="fc-admin__facts">
        <Counter label="Members" value={group.memberCount.toLocaleString()} />
        <Counter
          label="Files"
          value={(group.fileCount ?? 0).toLocaleString()}
        />
        <Counter
          label="Failed jobs"
          value={group.failedJobCount.toLocaleString()}
          tone={group.failedJobCount > 0 ? "danger" : undefined}
        />
      </dl>

      {group.lockedReason && (
        <p className="fc-admin__locked-reason">Locked: {group.lockedReason}</p>
      )}

      <StorageMeter
        label="Storage"
        limitBytes={limitBytes}
        byClass={{
          originals: group.usedOriginalsBytes ?? 0,
          derivatives: group.usedDerivativesBytes ?? 0,
          video: group.usedVideoBytes ?? 0,
          exports: group.usedExportBytes ?? 0,
        }}
      />

      {(group.reservedBytes ?? 0) > 0 && (
        <p className="fc-admin__reserved fc-tabular">
          {formatBytes(group.reservedBytes ?? 0)} is reserved by uploads in
          flight. It counts against the quota but is not stored yet.
        </p>
      )}

      <QuotaControls
        groupId={group.id}
        limitBytes={limitBytes}
        usedBytes={usedBytes}
      />
      <LockControl groupId={group.id} locked={group.status === "locked"} />

      {/* Members, escrow state and the bundle — the things a support case needs,
          on the screen where the group was already found. */}
      <GroupRecovery groupId={group.id} />
    </div>
  );
}

/* ───────────────────────────────── Quota ──────────────────────────────────── */

const BYTES_PER_GB = 1024 ** 3;

function QuotaControls({
  groupId,
  limitBytes,
  usedBytes,
}: {
  groupId: string;
  limitBytes: number;
  usedBytes: number;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const presets = useQuery(trpc.admin.quotaPresets.queryOptions());

  const [choice, setChoice] = useState<string>("custom");
  const [customGb, setCustomGb] = useState(
    String(Math.round(limitBytes / BYTES_PER_GB)),
  );
  const [totpCode, setTotpCode] = useState("");
  const [reason, setReason] = useState("");

  const selectedPreset = (presets.data ?? []).find(
    (preset) => preset.label === choice,
  );
  const limitFromCustom = Math.round(Number(customGb) * BYTES_PER_GB);
  const nextLimitBytes = selectedPreset
    ? selectedPreset.bytes
    : limitFromCustom;

  const valid =
    Number.isSafeInteger(nextLimitBytes) &&
    nextLimitBytes > 0 &&
    /^\d{6}$/.test(totpCode);

  const setQuota = useMutation({
    mutationFn: (input: {
      limitBytes: number;
      totpCode: string;
      reason?: string;
    }) => trpcClient.admin.setQuota.mutate({ groupId, ...input }),
    onSuccess: async (result) => {
      setTotpCode("");
      await queryClient.invalidateQueries({
        queryKey: trpc.admin.findGroup.queryKey({ groupId }),
      });
      toast(
        result.willBeOverLimit
          ? "Quota changed. The group is now over its limit — existing files are untouched, but uploads will be refused."
          : "Quota changed.",
        { variant: result.willBeOverLimit ? "info" : "success" },
      );
    },
  });

  return (
    <div className="fc-admin__subsection">
      <h4 className="fc-admin__subtitle">Storage limit</h4>
      <p className="fc-admin__hint">
        Currently {formatBytes(limitBytes)}, of which {formatBytes(usedBytes)}{" "}
        is in use. Lowering the limit below what is stored blocks new uploads;
        it never deletes anything.
      </p>

      {setQuota.isError && (
        <p className="fc-admin__error" role="alert">
          {errorMessage(setQuota.error)}
        </p>
      )}

      <form
        className="fc-admin__form"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmedReason = reason.trim();
          setQuota.mutate({
            limitBytes: nextLimitBytes,
            totpCode,
            ...(trimmedReason ? { reason: trimmedReason } : {}),
          });
        }}
      >
        <fieldset className="fc-admin__presets">
          <legend className="fc-admin__legend">Preset</legend>
          {(presets.data ?? []).map((preset) => (
            <label className="fc-admin__preset" key={preset.label}>
              <input
                type="radio"
                className="fc-sr-only"
                name={`fc-quota-${groupId}`}
                checked={choice === preset.label}
                onChange={() => setChoice(preset.label)}
              />
              <span>{preset.label}</span>
            </label>
          ))}
          <label className="fc-admin__preset">
            <input
              type="radio"
              className="fc-sr-only"
              name={`fc-quota-${groupId}`}
              checked={selectedPreset === undefined}
              onChange={() => setChoice("custom")}
            />
            <span>Custom</span>
          </label>
        </fieldset>

        {selectedPreset === undefined && (
          <Field label="Custom limit (GB)">
            <Input
              className="fc-admin__number"
              inputMode="numeric"
              value={customGb}
              onChange={(event) =>
                setCustomGb(event.target.value.replace(/[^\d.]/g, ""))
              }
            />
          </Field>
        )}

        <Field
          label="Reason"
          hint="Optional. Recorded in the audit log next to your name."
        >
          <Input
            value={reason}
            maxLength={200}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        <Field
          label="Authenticator code"
          hint="Changing a quota can stop a group working, so it always needs a second factor — a mistyped digit shouldn't be one keystroke from taking effect."
        >
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={totpCode}
            onChange={(event) =>
              setTotpCode(event.target.value.replace(/\D/g, ""))
            }
          />
        </Field>

        <p className="fc-admin__hint fc-tabular">
          New limit: <strong>{formatBytes(nextLimitBytes)}</strong>
        </p>

        <Button
          type="submit"
          variant="primary"
          loading={setQuota.isPending}
          disabled={!valid}
        >
          Set quota
        </Button>
      </form>
    </div>
  );
}

/* ───────────────────────────────── Locking ────────────────────────────────── */

function LockControl({
  groupId,
  locked,
}: {
  groupId: string;
  locked: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [reason, setReason] = useState("");

  const setLocked = useMutation({
    mutationFn: (input: { locked: boolean; reason?: string }) =>
      trpcClient.admin.setLocked.mutate({ groupId, ...input }),
    onSuccess: async (_result, input) => {
      setReason("");
      await queryClient.invalidateQueries({
        queryKey: trpc.admin.findGroup.queryKey({ groupId }),
      });
      toast(
        input.locked
          ? "Group locked — it is now read-only."
          : "Group unlocked.",
        {
          variant: "success",
        },
      );
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  return (
    <div className="fc-admin__subsection">
      <h4 className="fc-admin__subtitle">{locked ? "Unlock" : "Lock"}</h4>
      <p className="fc-admin__hint">
        A locked group is read-only for every member, including its owner.
        Nothing is deleted and nothing is hidden.
      </p>

      <form
        className="fc-admin__form"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = reason.trim();
          setLocked.mutate({
            locked: !locked,
            ...(!locked && trimmed ? { reason: trimmed } : {}),
          });
        }}
      >
        {!locked && (
          <Field
            label="Reason"
            hint="Optional. Shown to the group in the banner explaining the lock."
          >
            <Input
              value={reason}
              maxLength={200}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        )}

        <Button
          type="submit"
          variant={locked ? "secondary" : "danger"}
          loading={setLocked.isPending}
        >
          {locked ? "Unlock group" : "Lock group"}
        </Button>
      </form>
    </div>
  );
}

/* ────────────────────────────────── Jobs ──────────────────────────────────── */

/**
 * Whether the background services can actually be reached.
 *
 * Here because of a failure that had no symptom anywhere else. Both workers
 * were allowed to scale to zero, the doorbell that wakes them was pointed at
 * the wrong port, and enqueuing kept succeeding — so every service reported
 * itself healthy while every video and every unsealed photograph sat on
 * "Processing" for days. Nothing in this panel could have told you.
 *
 * Placed above the job list rather than below it because it explains the job
 * list: a screen full of `queued` means something different when nobody is
 * listening.
 */
function WakeTargets() {
  const status = useQuery(trpc.admin.wakeTargets.queryOptions());

  if (status.isLoading || status.isError) return null;
  if (status.data?.inProcess) {
    return (
      <p className="fc-admin__hint">
        Jobs run inside the API process, so there is nothing to wake.
      </p>
    );
  }

  const targets = status.data?.targets ?? [];
  const broken = targets.filter((target) => target.reachable === false);
  /*
   * A missing wake URL is only a problem when there is a separate process to
   * wake. On the in-process queue the consumer is this very service, so there
   * is nothing to ring and nothing to configure — flagging it there would put a
   * red panel on every development machine and teach everyone to ignore it,
   * which is the one thing this must not become.
   */
  const unconfigured =
    status.data?.driver === "bullmq"
      ? targets.filter((target) => target.url === null)
      : [];

  if (broken.length === 0 && unconfigured.length === 0) return null;

  return (
    <div className="fc-admin__notice" data-tone="danger" role="status">
      <strong>Background services may not be woken</strong>
      <p>
        Work is not lost — every job is in the queue — but nothing will run
        until something else wakes the service.
      </p>
      <ul>
        {broken.map((target) => (
          <li key={target.queue}>
            <code>{target.queue}</code> — {target.url} is not answering
            {target.lastError ? `: ${target.lastError}` : ""}
          </li>
        ))}
        {unconfigured.map((target) => (
          <li key={target.queue}>
            <code>{target.queue}</code> — no wake URL is configured
          </li>
        ))}
      </ul>
      <p>
        Check <code>WORKER_WAKE_URL</code> and{" "}
        <code>MEDIA_WORKER_WAKE_URL</code> on the API service. The port must be
        written out in full; <code>$PORT</code> there resolves to the
        API&rsquo;s own port, not the worker&rsquo;s.
      </p>
    </div>
  );
}

type JobFilter = "all" | "queued" | "active" | "failed";

function JobsSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState<JobFilter>("failed");

  const jobsInput: { state?: "queued" | "active" | "failed"; limit: number } =
    filter === "all" ? { limit: 25 } : { state: filter, limit: 25 };

  const jobs = useQuery(trpc.admin.jobs.queryOptions(jobsInput));

  const retry = useMutation({
    mutationFn: (input: { jobId: string }) =>
      trpcClient.admin.retryJob.mutate(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: trpc.admin.jobs.queryKey(jobsInput),
      });
      toast(
        "Job requeued. Processing is idempotent, so a re-run is always safe.",
        {
          variant: "success",
        },
      );
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  /**
   * Put the whole backlog back in the queue.
   *
   * The bulk counterpart to the per-row retry, for the case that actually
   * happens: a background service was unreachable for a while and a few hundred
   * files are wedged. The bytes are all in storage and the quota is already
   * charged, so this costs nobody an upload.
   */
  const reprocess = useMutation({
    mutationFn: () => trpcClient.admin.reprocessStuck.mutate({ limit: 500 }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: trpc.admin.jobs.queryKey(jobsInput),
      });
      toast(
        result.found === 0
          ? "Nothing is stuck."
          : `Requeued ${String(result.requeued)} of ${String(result.found)} stuck files.`,
        { variant: result.found === 0 ? "success" : "success" },
      );
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  const rows = jobs.data?.jobs ?? [];

  return (
    <section className="fc-admin__section" aria-labelledby="fc-admin-jobs">
      <WakeTargets />

      <div className="fc-admin__section-head">
        <h2 className="fc-admin__section-title" id="fc-admin-jobs">
          Recent processing jobs
        </h2>
        {/*
          Grouped so the head keeps two children. With three, `space-between`
          packs them onto one row and squeezes the filter into ellipses rather
          than wrapping.
        */}
        <div className="fc-admin__section-actions">
          <Button
            variant="secondary"
            onClick={() => reprocess.mutate()}
            disabled={reprocess.isPending}
          >
            {reprocess.isPending ? "Requeueing…" : "Requeue everything stuck"}
          </Button>
          <SegmentedControl<JobFilter>
            aria-label="Filter jobs by state"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "failed", label: "Failed" },
              { value: "queued", label: "Queued" },
              { value: "active", label: "Active" },
              { value: "all", label: "All" },
            ]}
          />
        </div>
      </div>

      {jobs.isLoading ? (
        <div className="fc-admin__rows" aria-hidden="true">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} height="var(--fc-row-height)" radius="sm" />
          ))}
        </div>
      ) : jobs.isError ? (
        <p className="fc-admin__error" role="alert">
          {errorMessage(jobs.error)}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<CheckIcon />}
          title={filter === "failed" ? "Nothing has failed" : "Nothing here"}
          description={
            filter === "failed"
              ? "Every job the workers have picked up recently has finished. This is the state you want the queue in."
              : "No jobs match that filter right now."
          }
        />
      ) : (
        /* Explicit roles: the mobile layout changes these cells' `display`,
           which would otherwise strip the table semantics. */
        <table className="fc-admin__table" role="table">
          <thead>
            <tr role="row">
              <th role="columnheader" scope="col">
                Job
              </th>
              <th role="columnheader" scope="col">
                Type
              </th>
              <th role="columnheader" scope="col">
                State
              </th>
              <th role="columnheader" scope="col">
                Created
              </th>
              <th role="columnheader" scope="col">
                <span className="fc-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((job) => (
              <tr role="row" key={job.id}>
                <td role="cell" data-label="Job">
                  <span className="fc-admin__job-id fc-tabular">{job.id}</span>
                  {job.error && (
                    <span className="fc-admin__job-error">{job.error}</span>
                  )}
                </td>
                <td role="cell" data-label="Type">
                  {job.type}
                </td>
                <td role="cell" data-label="State">
                  <Badge
                    variant={job.state === "failed" ? "danger" : undefined}
                  >
                    {job.state}
                  </Badge>
                  {job.attempts > 0 && (
                    <span className="fc-admin__attempts fc-tabular">
                      {job.attempts}{" "}
                      {job.attempts === 1 ? "attempt" : "attempts"}
                    </span>
                  )}
                </td>
                <td role="cell" data-label="Created" className="fc-tabular">
                  {formatDateTime(job.createdAt)}
                </td>
                <td role="cell" data-label="">
                  {job.state === "failed" && (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={
                        retry.isPending && retry.variables?.jobId === job.id
                      }
                      onClick={() => retry.mutate({ jobId: job.id })}
                    >
                      Retry
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ────────────────────────────────── Shared ────────────────────────────────── */

function Counter({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "danger";
}) {
  return (
    <div className="fc-admin__counter" data-tone={tone}>
      <dt className="fc-admin__counter-label">{label}</dt>
      <dd className="fc-admin__counter-value fc-tabular">{value}</dd>
      {hint && <dd className="fc-admin__counter-hint fc-tabular">{hint}</dd>}
    </div>
  );
}

const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

/** Dates cross the wire as strings but are typed as `Date`; accept either. */
function formatDateTime(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? "—" : DATE_TIME_FORMAT.format(date);
}

function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 12.3l2.5 2.5 4.5-5" />
    </svg>
  );
}
